import React, { useState } from 'react';
import { GestureResponderEvent, Pressable } from 'react-native';
import type { IconProps } from '../../icons/types';
import { calendarStyles } from './calendar.styles';
import { Colors } from '../../../../themes/colors';

export interface NavButtonProps {
  /** Icon component (e.g. `ChevronLeftIcon` / `ChevronRightIcon`). */
  icon: React.ComponentType<IconProps>;
  /** Required accessible name — this is an icon-only control. */
  accessibilityLabel: string;
  /** Press handler (prev / next / today). */
  onPress: (e: GestureResponderEvent) => void;
  /** Icon size in px. @default 16 */
  iconSize?: number;
  /** Test ID for end-to-end selectors. */
  testID?: string;
}

/**
 * Toolbar prev/next/today icon button (plan 0017 §1 "Toolbar"). 28×28, rounded-md,
 * centered icon, soft-slate hover fill — mirrors gripp-OS's `NavBtn` (`size-7`,
 * `rounded-md`, `hover:bg-secondary`). Icon-only, so `accessibilityLabel` is required.
 */
export const NavButton: React.FC<NavButtonProps> = ({
  icon: Icon,
  accessibilityLabel,
  onPress,
  iconSize = 16,
  testID,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={[calendarStyles.navButton, hovered && calendarStyles.navButtonHovered]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      <Icon size={iconSize} color={Colors.role.textSoft} />
    </Pressable>
  );
};

NavButton.displayName = 'NavButton';
